import {
  AfterViewInit,
  ElementRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Transaction, Bucket, EditTransactionAction, PlaidAccountSummary, TransactionBankMeta } from 'src/app/types';
import { TransactionService } from 'src/app/services/transaction.service';

import { TransactionDataSource } from '../transaction-data-source.class';
import {
  effectiveTransactionPlaidAccount,
  transactionInstitutionLogoSrc,
  transactionPlaidAccountDisplayName,
} from '../transaction-plaid-account-display';
import { CurrencyPipe } from '@angular/common';
import { NgIcon } from '@ng-icons/core';
import { HlmIcon } from '../../ui/helm/icon/src/lib/hlm-icon';
import { BucketSelectorComponent } from '../../shared/bucket-selector/bucket-selector.component';
import { DrawerService } from '../../services/drawer.service';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { TransactionRuleSuggestionLookupService } from '../../transaction-rules/transaction-rule-suggestion-lookup.service';

@Component({
  selector: 'transaction-list-item-content',
  templateUrl: './transaction-list-item-content.component.html',
  styleUrl: './transaction-list-item-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, HlmIcon, BucketSelectorComponent, CurrencyPipe, UiIconComponent],
})
export class TransactionListItemContentComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  transactionService = inject(TransactionService);

  readonly transaction = input.required<Transaction>();
  readonly transactionDataSource = input<TransactionDataSource | null>(null);
  readonly buckets = input<Bucket[]>([]);
  readonly bucketIdentities = input<Bucket[]>([]);
  readonly forceEditing = input(false);
  readonly showBucketSelector = input(true);
  readonly interactive = input(true);
  readonly selectedBucketChange = output<Bucket>();
  readonly editRequested = output<void>();

  readonly assignedBucket = computed(() => {
    const bucketId = this.transaction().bucketId;
    return bucketId ? this.bucketIdentityById(bucketId) : undefined;
  });

  readonly selectedBucket = signal<Bucket | undefined>(undefined);

  readonly needsSaving = signal(false);

  /**
   * Assigns a budget created from this row's "+ Add new budget" action. It writes to the data
   * source rather than this component, because the virtual list destroys off-screen rows and the
   * user can scroll this row away while the add-budget drawer is open.
   */
  readonly createdBucketTarget = computed(() => {
    const transaction = this.transaction();
    const transactionDataSource = this.transactionDataSource();
    if (!transactionDataSource?.setPendingBucket) return undefined;
    return (bucket: Bucket) => {
      transaction.selectedBucket = bucket;
      transactionDataSource.setPendingBucket(transaction.id, bucket);
    };
  });

  editingInTransactionList$ = this.transactionService.editingInTransactionList$;

  private readonly drawer = inject(DrawerService);
  private readonly ruleSuggestions = inject(TransactionRuleSuggestionLookupService);

  private destroy$ = new Subject<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private visibilityObserver?: IntersectionObserver;
  private suggestionVisible = false;

  ngAfterViewInit() {
    if (typeof IntersectionObserver === 'undefined') return;
    this.visibilityObserver = new IntersectionObserver(entries => {
      this.suggestionVisible = entries.some(entry => entry.isIntersecting);
      this.recordVisibleSuggestion();
    });
    this.visibilityObserver.observe(this.host.nativeElement);
  }

  private recordVisibleSuggestion() {
    const transaction = this.transaction();
    if (
      this.suggestionVisible &&
      this.showBucketSelector() &&
      this.interactive() &&
      !transaction.bucketId &&
      transaction.suggestedBucketIds?.[0] === this.selectedBucket()?.id
    )
      this.transactionDataSource()?.recordSuggestionDisplayed?.(transaction);
  }

  ngOnInit(): void {
    this.setup();
    this.subscribeToPendingBuckets();
  }

  ngOnChanges() {
    this.setup();
    this.recordVisibleSuggestion();
  }

  ngOnDestroy(): void {
    this.visibilityObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setup() {
    const transaction = this.transaction();
    if (!transaction) return;

    const transactionId = transaction.id;
    this.selectedBucket.set(undefined);
    this.needsSaving.set(false);

    if (transaction.bucketId) {
      this.setSelectedBucketFromData(this.bucketIdentityById(transaction.bucketId), transactionId);
      return;
    }

    if (transaction.selectedBucket) {
      this.selectedBucket.set(transaction.selectedBucket);
      this.needsSaving.set(true);
      this.registerPendingBucket(transaction.selectedBucket);
      return;
    }

    const suggestedBucketId = transaction.suggestedBucketIds?.[0];
    if (suggestedBucketId) this.setSelectedBucketFromData(this.bucketById(suggestedBucketId), transactionId, true);
  }

  private subscribeToPendingBuckets() {
    const transactionDataSource = this.transactionDataSource();
    if (!transactionDataSource?.pendingBuckets$) return;
    transactionDataSource.pendingBuckets$.pipe(takeUntil(this.destroy$)).subscribe(pending => {
      const transaction = this.transaction();
      if (!transaction) return;
      const override = pending.get(transaction.id);
      if (override && override.id !== this.selectedBucket()?.id) {
        this.selectedBucket.set(override);
        this.needsSaving.set(true);
      }
    });
  }

  bucketById(bucketId: string): Bucket | undefined {
    return this.buckets().find(bucket => bucket.id === bucketId);
  }

  bucketIdentityById(bucketId: string): Bucket | undefined {
    return this.bucketIdentities().find(bucket => bucket.id === bucketId) ?? this.bucketById(bucketId);
  }

  merchantDisplayName(transaction: Transaction) {
    return transaction.merchantMeta?.displayName || transaction.merchant;
  }

  isIncome(transaction: Transaction): boolean {
    return transaction.type === 'INCOME' || (transaction.netValue ?? 0) > 0;
  }

  accountDisplayName(account: PlaidAccountSummary): string {
    return transactionPlaidAccountDisplayName(account);
  }

  effectivePlaidAccount(transaction: Transaction): PlaidAccountSummary | undefined {
    return effectiveTransactionPlaidAccount(transaction);
  }

  effectiveBankMeta(transaction: Transaction): TransactionBankMeta | undefined {
    return transaction.bankMeta ?? transaction.parent?.bankMeta;
  }

  institutionLogoSrc(account: PlaidAccountSummary): string | null {
    return transactionInstitutionLogoSrc(account);
  }

  private readonly failedLogoSrcs = new Set<string>();

  logoFailed(src: string): boolean {
    return this.failedLogoSrcs.has(src);
  }

  onLogoError(src: string): void {
    this.failedLogoSrcs.add(src);
  }

  changeSelectedBucket(bucket: Bucket) {
    this.selectedBucket.set(bucket);
    this.needsSaving.set(this.transaction().bucketId !== bucket.id || !this.transaction().bucketId);
    if (this.needsSaving()) this.registerPendingBucket(bucket);
    else this.clearPendingBucket();
    this.selectedBucketChange.emit(bucket);
  }

  private registerPendingBucket(bucket: Bucket) {
    const transaction = this.transaction();
    const transactionDataSource = this.transactionDataSource();
    if (!transactionDataSource?.setPendingBucket || !transaction) return;
    transactionDataSource.setPendingBucket(transaction.id, bucket);
  }

  private clearPendingBucket() {
    const transaction = this.transaction();
    const transactionDataSource = this.transactionDataSource();
    if (!transactionDataSource?.setPendingBucket || !transaction) return;
    transactionDataSource.setPendingBucket(transaction.id, null);
  }

  private setSelectedBucketFromData(bucket: Bucket | undefined, transactionId: string, needsSaving = false) {
    if (this.transaction()?.id !== transactionId) return;
    if (!bucket) return;
    this.selectedBucket.set(bucket);
    this.needsSaving.set(needsSaving);
  }

  requestEdit(): void {
    if (this.interactive()) this.editRequested.emit();
  }

  suggestedRule() {
    if (!this.interactive()) return null;
    return this.ruleSuggestions.suggestionForTransaction(this.transaction());
  }

  openSuggestedRule(event: Event): void {
    event.stopPropagation();
    const suggestion = this.suggestedRule();
    if (!suggestion) return;
    this.drawer.openTransactionRuleDrawer({ suggestionId: suggestion.id, transactionId: this.transaction().id });
  }

  onSaveBucket(bucket: Bucket) {
    const transactionDataSource = this.transactionDataSource();
    if (!transactionDataSource) return;
    const transactionModel = {
      ...this.transaction(),
      bucketId: bucket.id,
    };
    this.needsSaving.set(false);
    this.clearPendingBucket();

    const selectedBucket = this.selectedBucket();
    if (selectedBucket) transactionModel.bucketId = selectedBucket.id;

    const action: EditTransactionAction = {
      type: 'EDIT_TRANSACTION',
      value: transactionModel,
    };

    return transactionDataSource.dispatch(action);
  }
}
